'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import CommissionStats from './CommissionStats';
import BookingsList from './BookingsList';
import PartnerPerformance from './PartnerPerformance';
import PayoutHistory from './PayoutHistory';

type TabType = 'overview' | 'bookings' | 'partners' | 'payouts';

const CommissionDashboardInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [dateRange, setDateRange] = useState('30days');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: 'ChartBarIcon' },
    { id: 'bookings' as TabType, label: 'Bookings', icon: 'TicketIcon' },
    { id: 'partners' as TabType, label: 'Partners', icon: 'BuildingOffice2Icon' },
    { id: 'payouts' as TabType, label: 'Payouts', icon: 'BanknotesIcon' },
  ];

  const dateRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'year', label: 'This Year' },
  ];

  if (!isHydrated) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse space-y-8">
          <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-black text-foreground mb-2">
              Commission Dashboard
            </h1>
            <p className="text-lg text-muted-foreground">
              Track your affiliate earnings from booking partnerships
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CalendarIcon" size={20} className="text-muted-foreground" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground bg-white hover:bg-surface transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-brand mb-8 border border-border overflow-hidden">
        <div className="flex flex-wrap border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 lg:px-6 py-4 font-bold transition-all duration-200 ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-surface'
              }`}
            >
              <Icon name={tab.icon as any} size={20} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4 lg:p-6">
          {activeTab === 'overview' && <CommissionStats dateRange={dateRange} />}
          {activeTab === 'bookings' && <BookingsList dateRange={dateRange} />}
          {activeTab === 'partners' && <PartnerPerformance dateRange={dateRange} />}
          {activeTab === 'payouts' && <PayoutHistory dateRange={dateRange} />}
        </div>
      </div>

      {/* Demo Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <Icon name="InformationCircleIcon" size={24} className="text-amber-600 flex-shrink-0" />
        <div>
          <h3 className="font-bold text-amber-900 mb-1">Demo Data</h3>
          <p className="text-sm text-amber-800">
            This dashboard displays test commission data. Real earnings will appear once you configure your affiliate IDs and customers complete bookings through your links.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommissionDashboardInteractive;
